import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import { BENCHMARK_SINGLECONTACT, DATE_FORMAT_UPDATED } from "../../constants/app.constants";
import {
  updateSingleApprovalScheduled,
  fetchApprovalScheduled,
} from "../scheduling/scheduling.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";

type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  approveId: number;
};

type UpdatedValue = string | Date;

const SchedulingApprovalModal: FC<OverviewModalProps> = ({
  approveId,
  isOpen,
  onClose,
  onSubmit
}) => {
  const [estimatedYardage, setEstimatedYardage] = useState("");
  const [notExceedYardage, setNotExceedYardage] = useState("");
  const [overageApprovedBy, setOverageApprovedBy] = useState("");
  const [dateOverageApproved, setDateOverageApproved] = useState<Dayjs | null>(null);
  const [validationMsg, setValidationMsg] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);

  const { isFetching, data: dataBenchmarkSingleApproval } = useQuery({
    queryKey: [BENCHMARK_SINGLECONTACT, approveId],
    queryFn: () => fetchApprovalScheduled(approveId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "estimatedYardage") {
        setEstimatedYardage(updatedValue as string);
      } else if (id === "notExceedYardage") {
        setNotExceedYardage(updatedValue as string);
      } else if (id === "overageApprovedBy") {
        setOverageApprovedBy(updatedValue as string);
      }
    },
    []
  );

  useEffect(() => {
    if (approveId && dataBenchmarkSingleApproval) {
      setEstimatedYardage(dataBenchmarkSingleApproval?.EstimatedYardage ?? "");
      setNotExceedYardage(dataBenchmarkSingleApproval?.NotExceedYardage ?? "");
      setOverageApprovedBy(dataBenchmarkSingleApproval?.OverageApprovedBy ?? "");
      setDateOverageApproved(
        dataBenchmarkSingleApproval?.DateOverageApproved
          ? dayjs(dataBenchmarkSingleApproval.DateOverageApproved)
          : null
      );
      setIsShowLoader(false);
    }
  }, [dataBenchmarkSingleApproval, approveId, isFetching, isOpen]);

  const handleSubmit = async () => {
    setValidationMsg("");

    if (!overageApprovedBy.trim()) {
      setValidationMsg("Please Enter Approved By");
      return;
    }

    if (!dateOverageApproved) {
      setValidationMsg("Please Enter Date Approved");
      return;
    }

    if (approveId) {
      setIsShowLoader(true);
      try {
        const updateResult = await updateSingleApprovalScheduled(
          approveId,
          estimatedYardage,
          notExceedYardage,
          overageApprovedBy,
          dateOverageApproved.toDate()
        );

        setIsShowLoader(false);

        if (updateResult) {
          Swal.fire({
            customClass: {
              popup: "sweetAlert",
            },
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: SCHEDULING_DIALOGS.approvalUpdateSuccess,
          });
          // handleCancel(); // Clear fields and close
          onSubmit()
        } else {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title: SCHEDULING_DIALOGS.approvalUpdateFailed,
          });
        }
      } catch (err) {
        setIsShowLoader(false);
        console.error("Error updating approval:", err);
        Swal.fire({
          icon: "error",
          title: SCHEDULING_DIALOGS.approvalUpdateError,
        });
      }
    }
  };

  const handleCancel = () => {
    setEstimatedYardage("");
    setNotExceedYardage("");
    setOverageApprovedBy("");
    setDateOverageApproved(null);
    setValidationMsg("");
    setIsShowLoader(false);
    onClose();
  };

  return (
    <Modal show={isOpen} label="Overage Approval" onClose={handleCancel}>
      {validationMsg && <div className="text-danger">{validationMsg}</div>}

      <div className="requestModalSection">
        <div className="modalForm">
          {isShowLoader || isFetching ? <Loader /> : null}
          <form>
            <div className="modal-body">
              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">Estimated Yardage:</label>
                <div className="col-md-4">{estimatedYardage}</div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">Not to Exceed Yardage:</label>
                <div className="col-md-4">{notExceedYardage}</div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Overage Approved By:<span className="text-danger">*</span>
                </label>
                <div className="col-md-4">
                  <TextField
                    className="date-input-field"
                    id="overageApprovedBy"
                    onChange={onChangeFieldValues}
                    type="text"
                    value={overageApprovedBy}
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Date Overage Approved:<span className="text-danger">*</span>
                </label>
                <div className="col-md-6">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Select date"
                      value={dateOverageApproved}
                      onChange={(newVal) => setDateOverageApproved(newVal)}
                      disableFuture
                      format={DATE_FORMAT_UPDATED}
                      slotProps={{
                        textField: {
                          size: "small",
                          className: "date-input-field"
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <div className="form-group row">
                <div className="col-md-4" />
                <div className="col-md-8 actionButtons">
                  <button
                    type="button"
                    className="btn cancelBtn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success submitBtn"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingApprovalModal;
